/**
 * Workflow-Cache Logger
 *
 * The package must work in any environment (server, CLI), so logging goes
 * through the environment-agnostic `BrowserLogger` interface from
 * `@chatsdet/browser-core` instead of importing pino directly.
 *
 * - Cloud (apps/api): call `setWorkflowCacheLogger(pinoToBrowserLogger(...))`
 *   once at boot so package logs flow into the structured pino pipeline.
 * - CLI (canary-cli): the `consoleLogger` default applies.
 *
 * `createLogger(bindings)` mirrors the apps/api pino-logger call shape
 * (`logger.info(message, payload?)`) so moved modules only change their
 * import path.
 *
 * @module logger
 */

import { consoleLogger, type BrowserLogger } from '@chatsdet/browser-core';

let activeLogger: BrowserLogger = consoleLogger;

/** Install an environment-specific logger (e.g. a pino adapter on the server). */
export function setWorkflowCacheLogger(logger: BrowserLogger): void {
  activeLogger = logger;
}

/**
 * Create a logger that merges `bindings` into every payload and delegates to
 * the active environment logger. Same `(message, payload?)` call shape as the
 * apps/api `createLogger`.
 */
export function createLogger(bindings?: Record<string, unknown>): BrowserLogger {
  if (!bindings || Object.keys(bindings).length === 0) {
    return {
      debug: (msg, data) => activeLogger.debug(msg, data),
      info: (msg, data) => activeLogger.info(msg, data),
      warn: (msg, data) => activeLogger.warn(msg, data),
      error: (msg, data) => activeLogger.error(msg, data),
    };
  }
  const merge = (data?: Record<string, unknown>) => ({ ...bindings, ...data });
  return {
    debug: (msg, data) => activeLogger.debug(msg, merge(data)),
    info: (msg, data) => activeLogger.info(msg, merge(data)),
    warn: (msg, data) => activeLogger.warn(msg, merge(data)),
    error: (msg, data) => activeLogger.error(msg, merge(data)),
  };
}
